import type { Http2ServerRequest } from 'node:http2'

import { type Sso, SsoType } from '@bettermode/common/gql/generated'
import { DomainPathInfo } from '@tribeplatform/react-components/common/utils'

import { RuntimeConfigs } from '../../../../../configs/runtime.js'
import { SsoStateType } from '../../../types/server.types.js'
import {
  SSO_CALLBACK_PATH,
  SSO_FORWARDER_PATH,
} from '../constants/ssos.constants.js'

export const rewriteToSsoCallbackUrl = (requestUrl: string): string => {
  if (requestUrl.startsWith(SSO_FORWARDER_PATH)) {
    return requestUrl.replace(SSO_FORWARDER_PATH, SSO_CALLBACK_PATH)
  }

  return `${SSO_CALLBACK_PATH}${requestUrl}`
}

export const getSsoCallbackUrl = (
  type: SsoType,
  getResourceUrl: DomainPathInfo['getResourceUrl'],
  redirect?: string,
): string => {
  const sharedSsoCallbackPath = `${RuntimeConfigs.SSOS_FORWARDER_SITE}${SSO_FORWARDER_PATH}`
  const sharedSsoCallbackUrl = `${sharedSsoCallbackPath}?redirect_uri=${
    redirect || '/'
  }`

  switch (type) {
    case SsoType.jwt:
      /**
       * I know. `sso` is too generic, but it’s a legacy thing and the path only handles
       * JWT authentication.
       * @CAUTION JWT SSO does not hit this path and is being handled differently.
       */
      return getResourceUrl('/api/auth/sso')

    case SsoType.Saml:
      return getResourceUrl('/api/auth/saml')

    case SsoType.oauth2:
      return getResourceUrl(
        `${SSO_CALLBACK_PATH}${redirect ? `?redirect_uri=${redirect}` : ''}`,
      )

    case SsoType.Apple:
    case SsoType.google:
    case SsoType.facebook:
    case SsoType.linkedin:
    case SsoType.Discord:
    case SsoType.Slack:
      return sharedSsoCallbackUrl

    default: {
      const _exhaustiveCheck: SsoType.jwt = type
      return sharedSsoCallbackUrl
    }
  }
}

const CUSTOM_SSO_PRIORITIES = [SsoType.oauth2, SsoType.jwt, SsoType.Saml]
export const sortSsosOptions = <T extends Array<{ type: Sso['type'] }>>(
  ssos: T,
): T[number][] => {
  return [...ssos].sort((a, b) => {
    const aIndex = CUSTOM_SSO_PRIORITIES.indexOf(a.type)
    const bIndex = CUSTOM_SSO_PRIORITIES.indexOf(b.type)

    if (aIndex === -1 && bIndex === -1) {
      return 0
    }

    if (aIndex === -1) {
      return 1
    }

    if (bIndex === -1) {
      return -1
    }

    return aIndex - bIndex
  })
}

export const isCustomSso = (sso: { type: Sso['type'] }): boolean => {
  return CUSTOM_SSO_PRIORITIES.includes(sso.type)
}

export const isFunctioningSsoOption = (sso: {
  type: Sso['type']
  authorizationUrl: Sso['authorizationUrl']
  signUpUrl: Sso['signUpUrl']
  tokenUrl: Sso['tokenUrl']
}): boolean => {
  if (sso.type === SsoType.jwt) {
    return !!sso.authorizationUrl || !!sso.signUpUrl
  }

  if (sso.type === SsoType.Saml) {
    /**
     * Apparently we haven't prevented customers from shooting themselves in the foot
     * and they can enable SAML without a tokenUrl (?)
     */
    return !!sso.tokenUrl
  }

  return true
}

export const isVisibleSsoOption = (sso: {
  type: Sso['type']
  buttonText: Sso['buttonText']
}): boolean => {
  if (isCustomSso(sso)) {
    /**
     * Because JWT is a special snowflake with hardcoded fallback text!
     */
    if (sso.type === SsoType.jwt) {
      return true
    }

    return !!sso.buttonText
  }

  return true
}

export enum AuthOperationType {
  'login' = 'login',
  'signup' = 'signup',
}

export const getAuthOperationType = (
  apiPath: Http2ServerRequest['path'],
): AuthOperationType => {
  if (apiPath.endsWith('/auth/signup')) {
    return AuthOperationType.signup
  }
  return AuthOperationType.login
}

export const parseSsoState = (state: string): Partial<SsoStateType> => {
  if (!state) {
    return null
  }

  try {
    const decodedState = atob(state)
    const uriDecodedState = decodeURIComponent(decodedState)
    const parsedState = JSON.parse(uriDecodedState) as SsoStateType
    const SsoNetworkDomain = parsedState?.networkDomain

    if (!SsoNetworkDomain) {
      return null
    }

    return parsedState
  } catch (error) {
    return null
  }
}
